import { useEffect, useState } from 'react';
import type { IDiagnosticReportGetOne } from '../../interfaces/diagnosticReportGetOne';
import type { IProcess, IProcessDiagnosticReport } from '../../interfaces/process';
import type { IRequestGetOne } from '../../interfaces/requestGetOne';
import type { IReportItemState } from '../episodes/Episode';
import { fetchJson } from '../../../store/fetch';
import { LoadingTwo } from '../../elements/common';
import { ProcessContainer } from '../episodes/common';
import { ReloadEpisode } from '../episodes/reloadEpisode';
import { ClarificationItemOpen } from './ClarificationItemOpen';

interface IClarificationItemProps {
    diagnosticReport: IProcessDiagnosticReport;
    isLastItem: boolean;
    isPreviousRequest: boolean;
    itemState: IReportItemState;
    onLoaded: () => void;
    process: IProcess;
    reloadSelfOrAllProcesses: (reload: ReloadEpisode) => Promise<void>;
    updateItemState: (itemState: IReportItemState) => Promise<void>;
}

export function ClarificationItem(props: IClarificationItemProps) {
    const [diagnosticReport, setDiagnosticReport] = useState<IDiagnosticReportGetOne>();
    const [request, setRequest] = useState<IRequestGetOne>();

    useEffect(() => {
        load(props.process.id, props.diagnosticReport.id);

        props.onLoaded();
    }, []);

    useEffect(() => {
        if (props.itemState.initialIsOpen) {
            load(props.process.id, props.diagnosticReport.id);
        }
    }, [props.itemState.initialIsOpen, props.process.status, props.diagnosticReport.reviewStatus]);

    return (
        <ProcessContainer>
            <LoadingTwo
                item1={diagnosticReport}
                item2={request}
                loaded={(loadedDiagnosticReport, loadedRequest) => (
                    <ClarificationItemOpen
                        acceptDiagnosticReport={async () => {
                            await load(props.process.id, loadedDiagnosticReport.id);
                            await props.reloadSelfOrAllProcesses(ReloadEpisode.reloadAll);
                        }}
                        diagnosticReport={loadedDiagnosticReport}
                        isLastItem={props.isLastItem}
                        isPreviousRequest={props.isPreviousRequest}
                        itemState={props.itemState}
                        onSave={async () => await load(props.process.id, loadedDiagnosticReport.id)}
                        reloadProcess={async () => {
                            await load(props.process.id, loadedDiagnosticReport.id);
                            await props.reloadSelfOrAllProcesses(ReloadEpisode.reloadSelf);
                        }}
                        request={loadedRequest}
                        updateItemState={props.updateItemState}
                    />
                )}
            />
        </ProcessContainer>
    );

    async function load(requestId: string, diagnosticReportId: string) {
        await loadRequest(requestId);
        await loadDiagnosticReport(diagnosticReportId);
    }

    async function loadRequest(requestId: string) {
        const url =
            `/api/requests/${requestId}?showAssets=true` +
            '&includeItemAuthorizations=' +
            'canAddConsultDiagnosticReport,' +
            'canAddExaminationDiagnosticReport,' +
            'canBecomeRequester,' +
            'canAddReferral';

        const result = await fetchJson(url, 'GET');
        setRequest(result.json);
    }

    async function loadDiagnosticReport(diagnosticReportId: string) {
        if (props.itemState.mode !== 'add') {
            const url =
                `/api/diagnosticReports/${diagnosticReportId}?showAssets=true&includeItemAuthorizations=` +
                'canAddDiagnosticReportClarificationRequester,' +
                'canAddDiagnosticReportClarificationPerformer,' +
                'canUpdateDiagnosticReportToClosed,' +
                'canUpdate,' +
                'canTakeProcess,' +
                'canRequestMessageToGP';

            const result = await fetchJson(url, 'GET');
            setDiagnosticReport(result.json);
        }
    }
}
